import { Link } from "react-router-dom";
import { Instagram, Phone, Mail, MapPin } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useBusinessHours } from "@/hooks/useBusinessHours";

const Footer = () => {
  const { settings } = useSiteSettings();
  const { formatted: hourLines, loading: hoursLoading } = useBusinessHours();

  const instagramUrl = settings.instagram_handle.startsWith("@")
    ? `https://www.instagram.com/${settings.instagram_handle.slice(1)}`
    : `https://www.instagram.com/${settings.instagram_handle}`;

  return (
    <footer className="bg-secondary border-t border-border/50">
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16">
          {/* Brand */}
          <div>
            <h3 className="font-serif text-2xl font-bold mb-5 italic">
              Paradise <span className="text-primary not-italic">Beauty</span>
            </h3>
            <p className="text-muted-foreground text-sm leading-[1.9] font-extralight tracking-wide">
              Luxury beauty services — nails, lashes, pedicures & dermaplaning. Your beauty, pure passion.
            </p>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-xs font-light tracking-[0.4em] uppercase mb-5 text-primary">Hours</h4>
            <ul className="text-muted-foreground text-sm space-y-2">
              {hoursLoading && hourLines.length === 0 ? (
                <li className="opacity-50">Loading…</li>
              ) : (
                hourLines.map((line) => (
                  <li key={line.label}>
                    {line.label}: {line.isClosed ? <span className="italic">Closed</span> : line.value}
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-light tracking-[0.4em] uppercase mb-5 text-primary">Contact</h4>
            <ul className="text-muted-foreground text-sm space-y-3">
              <li className="flex items-center gap-2">
                <Phone size={14} />
                <a href={`tel:${settings.phone}`} className="hover:text-primary transition-colors">{settings.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={14} />
                <a href={`mailto:${settings.email}`} className="hover:text-primary transition-colors">{settings.email}</a>
              </li>
              <li className="flex items-center gap-2"><MapPin size={14} /> {settings.address}</li>
              <li className="flex items-center gap-2">
                <Instagram size={14} />
                <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                  {settings.instagram_handle}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-xs font-light tracking-[0.4em] uppercase mb-5 text-primary">Legal</h4>
            <ul className="text-muted-foreground text-sm space-y-3">
              <li><Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-of-use" className="hover:text-primary transition-colors">Terms of Use</Link></li>
              <li><Link to="/cancellation-policy" className="hover:text-primary transition-colors">Cancellation Policy</Link></li>
              <li><Link to="/rewards-policy" className="hover:text-primary transition-colors">Rewards & Referral Policy</Link></li>
              <li><Link to="/sms-consent" className="hover:text-primary transition-colors">SMS & Email Consent</Link></li>
              <li><Link to="/delete-account" className="hover:text-primary transition-colors">Delete Account</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border/50 mt-16 pt-10 text-center text-muted-foreground text-[10px] tracking-[0.4em] uppercase font-extralight">
          © {new Date().getFullYear()} In Paradise Beauty. All rights reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
